import {
  CommonResponse,
  CustomError,
} from "api/interfaces/commonInterface.interface";
import { notify } from "components/atoms/notification/Notification";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import { notification } from "antd";
import lang from "lang/en";
import reverseLang from "lang/reverse-en";

const useApiError = () => {
  const intl = useIntl();

  const handleError = useCallback(
    (
      restError: CustomError,
      defaultMessage: string,
      intlKey: string = "ServerErrorCode"
    ) => {
      const error = restError.response?.data.error;
      const translationKeyExists = !!intl.messages[`${intlKey}.${error}`];
      if (translationKeyExists) {
        notify(
          "error",
          intl.formatMessage({
            id: `${intlKey}.${error}`,
            defaultMessage: defaultMessage,
          })
        );
      } else {
        notify("error", defaultMessage);
      }
    },
    [intl]
  );
  const notifyStatus = useCallback(
    (message: string, type: "success" | "error" | "info" = "success") => {
      const msg = intlString(message);
      if (msg) {
        notify(type, msg);
      }
    },
    []
  );
  const notifySuccess = useCallback(
    (message: string) => {
      notifyStatus(message, "success");
    },
    [notifyStatus]
  );
  const notifyError = useCallback(
    (message: string) => {
      notifyStatus(message, "error");
    },
    [notifyStatus]
  );
  const notifyInfo = useCallback(
    (message: string) => {
      notifyStatus(message, "info");
    },
    [notifyStatus]
  );

  const intlString = useCallback(
    (key: string) => {
      const id = reverseLang[key];

      if (id) {
        return intl.formatMessage({
          id: id,
          defaultMessage: key,
        }) as string;
      }
      return key;
    },
    [intl]
  );

  const getErrorMessage = useCallback(
    (
      err: CustomError,
      message: string,
      intlKey: string = "ServerErrorCode"
    ) => {
      const error = err.response?.data?.error;
      const errorKey =
        error !== undefined && error !== null
          ? `${intlKey}.${String(error)}`
          : undefined;
      return errorKey
        ? (intl.formatMessage({
            id: errorKey,
            defaultMessage: message,
          }) as string)
        : message;
    },
    [intl]
  );

  const handleErrorNotify = useCallback(
    (
      err: CustomError,
      message: string,
      intlKey: string = "ServerErrorCode"
    ) => {
      notify("error", getErrorMessage(err, message, intlKey));
    },
    [getErrorMessage]
  );

  const handleErrorSubReason = useCallback(
    (
      restError: CustomError,
      defaultMessage: string,
      intlKey: string = "ServerErrorCode"
    ) => {
      const error = restError.response?.data.error;
      const translationKeyExists = !!intl.messages[`${intlKey}.${error}`];
      if (translationKeyExists) {
        notification.error({
          message: defaultMessage,
          description: intl.formatMessage({
            id: `${intlKey}.${error}`,
            defaultMessage: defaultMessage,
          }),
          placement: "topRight",
        });
      } else {
        notify("error", defaultMessage);
      }
    },
    [intl]
  );

  const displayError = useCallback(
    (
      restError: CustomError,
      defaultMessage: string,
      intlKey: string = "ServerErrorCode"
    ) => {
      return getErrorMessage(restError, defaultMessage, intlKey);
    },
    [getErrorMessage]
  );

  const createCustomError = useCallback(
    (data: CommonResponse, defaultMessage: string): CustomError => {
      const e: CustomError = {
        name: defaultMessage,
        message: defaultMessage,
        response: {
          data: {
            error: data.error,
            errorString: data.errorString,
            result: null,
          },
          status: 500,
          headers: "",
        },
      };
      return e;
    },
    []
  );

  return {
    handleError,
    getErrorMessage,
    handleErrorSubReason,
    displayError,
    createCustomError,
    intlString,
    lang,
    handleErrorNotify,
    notifySuccess,
    notifyError,
    notifyInfo,
  } as const;
};

export default useApiError;
